/**
 * Recently Viewed Products
 *
 * Stores viewed products in localStorage and renders them
 * on product pages (excluding the current product).
 * Enables tab switching when both recommended and recently viewed exist.
 */

import { init as initScrollRows } from "./scroll-row.js";

const STORAGE_KEY = "vanyx_recently_viewed";
const MAX_ITEMS = 8;

function escapeHtml(str) {
  if (!str) {
    return "";
  }
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isSafeUrl(url) {
  return typeof url === "string" && url.startsWith("/");
}

function getItems() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function saveItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function initTabs() {
  const wrapper = document.querySelector("[data-suggestions]");
  if (!wrapper) {
    return;
  }

  const recPanel = wrapper.querySelector('[data-panel="recommended"]');
  const rvPanel = wrapper.querySelector('[data-panel="recently-viewed"]');
  const hasRecommended = recPanel && !recPanel.hidden;
  const hasRecentlyViewed = rvPanel && !rvPanel.hidden;

  if (!hasRecommended && !hasRecentlyViewed) {
    wrapper.hidden = true;
    return;
  }

  // Only show tabs when both panels have content
  if (hasRecommended && hasRecentlyViewed) {
    const tabBar = wrapper.querySelector("[data-suggestion-tabs]");
    const heading = wrapper.querySelector("[data-suggestion-heading]");
    if (tabBar) {
      tabBar.hidden = false;
    }
    // Hide the standalone heading when tabs are shown
    if (heading) {
      heading.hidden = true;
    }
    // Recently viewed starts hidden
    rvPanel.hidden = true;

    // Tab click handler
    tabBar.addEventListener("click", (e) => {
      const tab = e.target.closest("[data-tab]");
      if (!tab) {
        return;
      }

      const target = tab.dataset.tab;
      tabBar.querySelectorAll("[data-tab]").forEach((t) => {
        t.classList.toggle("shop-suggestions__tab--active", t.dataset.tab === target);
      });

      recPanel.hidden = target !== "recommended";
      rvPanel.hidden = target !== "recently-viewed";
    });
  }
}

export function init() {
  const product = document.querySelector("[data-shopify-handle]");
  if (!product) {
    return;
  }

  const handle = product.dataset.shopifyHandle;
  const title = document.querySelector(".shop-product__details h1")?.textContent?.trim();
  const priceEl = document.querySelector("[data-price]");
  const price = priceEl ? priceEl.textContent.trim() : "";
  const imgEl = product.querySelector(".image-slider__slide img");
  const image = imgEl ? imgEl.getAttribute("src") : "";
  const url = window.location.pathname;

  // Render previously viewed (excluding current)
  const items = getItems().filter((item) => item.handle !== handle);
  const container = document.querySelector("[data-recently-viewed]");

  if (container && items.length > 0) {
    const lang = (window.VANYX_CONFIG || {}).lang || "de";
    container.innerHTML = `
      <div class="scroll-row" data-scroll-row>
        <button type="button" class="scroll-row__arrow scroll-row__arrow--prev" data-scroll-prev aria-label="${lang === "en" ? "Previous" : "Zurück"}">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <div class="scroll-row__track" data-scroll-track>
          ${items.filter((item) => isSafeUrl(item.url)).map((item) => `
            <a href="${escapeHtml(item.url)}" class="shop-card">
              ${item.image ? `<div class="shop-card__image"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" decoding="async" width="640" height="480" /></div>` : ""}
              <div class="shop-card__info">
                <h3 class="shop-card__title">${escapeHtml(item.title)}</h3>
                <p class="shop-card__price">${escapeHtml(item.price)} &euro;</p>
              </div>
            </a>
          `).join("")}
        </div>
        <button type="button" class="scroll-row__arrow scroll-row__arrow--next" data-scroll-next aria-label="${lang === "en" ? "Next" : "Weiter"}">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
    `;
    container.hidden = false;
    initScrollRows();
  }

  // Store current product
  if (handle && title) {
    const current = { handle, title, price, image, url };
    const updated = [current, ...getItems().filter((item) => item.handle !== handle)].slice(0, MAX_ITEMS);
    saveItems(updated);
  }

  // Initialize tabs after rendering
  initTabs();
}
